import { useEffect, useRef, useState } from "react";
import Card from "../../../elements/Card";
import { useParams } from "react-router-dom";
import useClickOutside from "../../../../hooks/useClickOutside";
import Radio from "../../../form/Radio";
import Input from "../../../form/Input";
import { t } from "i18next";

const ReadOnly = ({ data }) => {
  return (
    <Card bodyClass={"space-y-6"} className={" py-4 md:py-8 px-3 md:px-6"}>
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 md:col-span-6 space-y-4">
          <div className="space-y-2">
            <h3 className="font-semibold text-lg">{t("Event Visibility")}</h3>
            <div className="text-text-gradient-red font-medium">
              {data?.visibility ? (
                <div className="space-y-1">
                  <div className="capitalize font-semibold">
                    {data.visibility}
                  </div>
                  <div className="text-sm text-slate-600">
                    {data.visibility === "public"
                      ? "Shared on dancebrite and search engines"
                      : "Shared only with a select audience"}
                  </div>
                  {data.visibility === "private" && data?.password && (
                    <div className="text-sm text-slate-500">
                      {t("Password protected")}
                    </div>
                  )}
                </div>
              ) : (
                t("Not set")
              )}
            </div>
          </div>
        </div>

        <div className="col-span-12 md:col-span-6 space-y-4">
          <div className="space-y-2">
            <h3 className="font-semibold text-lg">{t("Refund Policy")}</h3>
            <div className="text-text-gradient-red font-medium">
              {data?.allowRefundPolicy !== undefined ? (
                <div className="space-y-1">
                  <div className="font-semibold">
                    {t(
                      data.allowRefundPolicy ? "Refunds Allowed" : "No Refunds"
                    )}
                  </div>
                  {data.allowRefundPolicy && data?.daysBeforeRefund && (
                    <div className="text-sm text-slate-600">
                      {t("Refunds available up to")} {data.daysBeforeRefund}{" "}
                      {t("days")}
                      {t("before the event")}
                    </div>
                  )}
                  <div className="text-xs text-slate-500">
                    {t(
                      data.allowRefundPolicy
                        ? "Attendees can request refunds within the specified timeframe"
                        : "Once purchased, tickets are non-refundable"
                    )}
                  </div>
                </div>
              ) : (
                t("Not set")
              )}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

const PublishSetting = ({
  values = {},
  handleChange = () => {},
  formValidate = () => {},
  errors,
  setFieldsValue = () => {},
  event,
  validation,
}) => {
  const divRef = useRef(null);
  const { eventId } = useParams();
  const [readOnly, setReadOnly] = useState(true);

  useClickOutside(divRef, () => {
    if (eventId) {
      setReadOnly(true);
    } else {
      setReadOnly(false);
    }
  });

  const hasErrors = () => {
    const errorFields = ["password"];
    return errorFields.some((field) => errors[field]);
  };

  useEffect(() => {
    if (hasErrors() && eventId) {
      setReadOnly(false);
    }
  }, [errors, eventId]);

  return (
    <div
      ref={divRef}
      onClick={() => {
        if (eventId) {
          setReadOnly(false);
        }
      }}
    >
      {readOnly ? (
        <ReadOnly data={values} />
      ) : (
        <Card
          children={t(
            "Your type and category help your event appear in more searches."
          )}
          bodyClass={"space-y-6"}
        >
          <div className="relative mb-5">
            <div className="text-lg font-semibold text-black mb-1">
              {t("Is your event public or private?")}
            </div>
            <div className="flex items-center gap-5">
              <div className="relative">
                <div className="text-xs text-slate-600 font-medium mb-1">
                  {t("Shared on dancebrite and search engines")}
                </div>
                <Radio
                  value="public"
                  radioName="visibility"
                  checked={values?.visibility === "public"}
                  onChange={(e) => {
                    const update_vallidation = { ...validation };
                    delete update_vallidation?.password;
                    handleChange({
                      target: { name: e.target.name, value: e.target?.value },
                    });
                  }}
                  radioLabel={t("Public")}
                  radioLableClass={"text-base text-slate-600"}
                />
              </div>
              <div className="relative">
                <div className="text-xs text-slate-600 font-medium mb-1">
                  {t("Shared only with a select audience")}
                </div>
                <Radio
                  value="private"
                  radioName="visibility"
                  checked={values?.visibility === "private"}
                  onChange={(e) => {
                    handleChange({
                      target: { name: e.target.name, value: e.target?.value },
                    });
                  }}
                  radioLabel={t("Private")}
                  radioLableClass={"text-base text-slate-600"}
                />
              </div>
              {values?.visibility === "private" && (
                <div className="relative flex-grow">
                  <Input
                    inputType="password"
                    inputPlaceholder={"Enter password"}
                    label={t("password")}
                    onChange={handleChange}
                    inputName={"password"}
                    value={values?.password}
                    {...formValidate(errors, "password")}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="relative mb-5 space-y-4">
            <div className="relative">
              <div className="text-lg font-semibold text-black ">
                {t("Set your refund policy")}
              </div>
              <div className="text-sm text-black">
                {t(
                  "Once your event is published, you can only update your policy to make it more flexible for your attendees."
                )}
              </div>
            </div>
            <div className="flex items-center gap-5">
              <div className="relative">
                <Radio
                  value={true}
                  radioName="allowRefundPolicy"
                  checked={values?.allowRefundPolicy === true}
                  onChange={(e) => {
                    handleChange({
                      target: { name: e.target.name, value: true },
                    });
                  }}
                  radioLabel={t("Allow refunds")}
                  radioLableClass={"text-base text-slate-600"}
                />
              </div>
              <div className="relative">
                <Radio
                  value={false}
                  radioName="allowRefundPolicy"
                  checked={values?.allowRefundPolicy === false}
                  onChange={(e) => {
                    handleChange({
                      target: { name: e.target.name, value: false },
                    });
                  }}
                  radioLabel={t("Don't allow refunds")}
                  radioLableClass={"text-base text-slate-600"}
                />
              </div>
            </div>
            {values?.allowRefundPolicy && (
              <div className="relative">
                <Input
                  label={t("Days before the event")}
                  labelClasses={"!text-base !text-slate-600 !font-normal"}
                  inputClasses={"h-12"}
                  value={values?.daysBeforeRefund}
                  onChange={handleChange}
                  inputName={"daysBeforeRefund"}
                  note={t(
                    "Set how many days (1 to 30) before the event that attendees can request refunds."
                  )}
                  noteClasses={"text-right text-xs"}
                  {...formValidate(errors, "daysBeforeRefund")}
                />
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

export default PublishSetting;
